import zod from 'zod';
import { stringToBoolean, stringToListOfEnum, stringToListOfNumber, zodLimit, zodPage } from './helpers';

export const occurrencePriorities = [
    'maximum',
    'high',
    'medium',
    'low',
] as const;

export type OccurrencePriorities = typeof occurrencePriorities[number];

export const occurrenceSituations = [
    'pending',
    'in_service',
    'ongoing',
    'concluded'
] as const;

export type OccurrenceSituation = typeof occurrenceSituations[number];

export const triggerTypes = [
    'face_detected',
    'plate_detected',
    'irregular_vehicle',
    'clone_suspect',
    'illegal_debris',
    'area_invasion',
    'scenechangedetection',
    'defocus',
    'mp_plate_detected',
    'mp_mobile_agent_plate_detected',
    'mp_face_detected',
    'alarm_center',
    'guardian_app',
    'school_app',
    'missing_face_detected',
    'sigrc',
    'cortex_plate_detected',
    'plate_detected_by_mobile_agent',
    'plate_detected_by_citizen_app'
] as const;

export type TriggerType = typeof triggerTypes[number];

export const handcuffReasonTypes = [
    'resistance',
    'fearOfEscape',
    'dangerToPhysicalIntegrity',
] as const;

export type HandcuffReasonType = typeof handcuffReasonTypes[number];

export const teamNameCategory = [
    'light_vehicle',
    'motorcycle',
    'on_foot',
    'bicycle',
    'boat',
    'heavy_vehicle',
    'tractor_truck',
    'van',
    'jet_ski',
    'bus'
] as const;
export type TeamNameCategory = typeof teamNameCategory[number];

export const unitStatus = [
    // All active will include active/displaced/operationalPause/activeAfterSchedule
    'allActive',
    'active',
    'displaced',
    'operationalPause',
    'waitingActivation',
    'waitingActivationBeforeSchedule',
    'activeAfterSchedule',
    'finished'
] as const;

export type UnitStatus = typeof unitStatus[number];

export const OccurrencesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    triggerTypeIds: stringToListOfEnum(zod.enum([
        ...triggerTypes,
        'manual',
    ])).optional(),
    priority: zod.enum(occurrencePriorities).optional(),
    situation: zod.enum(occurrenceSituations).optional(),
    occurrenceTypeId: stringToListOfNumber.optional(),
    responsibleUserId: zod.coerce.number().optional(),
    unitNamesIds: stringToListOfNumber.optional(),
    textFilter: zod.string().optional(),
    tags: stringToListOfNumber.optional(),
    actingBodyId: zod.coerce.number().optional(),
    mapPolygonIds: stringToListOfNumber.optional(),
    polygonType: zod.string().optional(),
    assumedBy: zod.coerce.number().optional(),
    transferredBy: zod.coerce.number().optional(),
    receivedBy: zod.coerce.number().optional(),
    finishedBy: zod.coerce.number().optional(),
    beginIn: zod.coerce.number().optional(),
    endIn: zod.coerce.number().optional(),
    actingBodyCommandIds: stringToListOfNumber.optional(),
    withDispatch: stringToBoolean.optional(),
    withoutDispatch: stringToBoolean.optional(),
    withOccurrenceDocument: stringToBoolean.optional(),
    withoutOccurrenceDocument: stringToBoolean.optional(),
    latitude: zod.coerce.number().optional(),
    longitude: zod.coerce.number().optional(),
    radius: zod.coerce.number().optional(),
    displaceReasonIds: stringToListOfNumber.optional(),
    finishReasonCategoryIds: stringToListOfNumber.optional(),
    finishReasonSubCategoryIds: stringToListOfNumber.optional(),
    actingBodyUnitIds: stringToListOfNumber.optional(),
    policeReport: zod.string().optional(),
    cameraTags: stringToListOfNumber.optional()
});

export const FinishCategoriesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

export const PoliceStationsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
});

export const CityPropertiesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

export const HandcuffReasonsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    type: zod.enum(handcuffReasonTypes).optional(),
});

export const UnitPauseReasonsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    actingBodyUnitId: zod.coerce.number().optional(),
});

export const UnitFinishEarlyReasonsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    actingBodyUnitId: zod.coerce.number().optional(),
});

export const OccurrenceTypesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    isActive: stringToBoolean,
});

export const EquipmentsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    unitId: zod.coerce.number().optional(),
});

export const TeamNamesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    actingBodyUnitId: zod.coerce.number().optional(),
    actingBodyCommandIds: stringToListOfNumber.optional(),
    isConnected: stringToBoolean.optional(),
});

export const TeamProgramsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

export const VehicleRelationsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

export const PersonRelationsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

export const ProvidencesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

export const DispatchReportsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    tags: stringToListOfNumber.optional(),
});

export const UnitDisplacedReasonsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

export const ObjectsCategoriesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

export const AssignmentsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

export const OccurrenceTagsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

export const ReportTagsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

export const UnitsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    teamFilter: zod.string().optional(),
    agentFilter: zod.string().optional(),
    status: stringToListOfEnum(zod.enum(unitStatus)).optional(),
    beginIn: zod.coerce.number().optional(),
    endIn: zod.coerce.number().optional(),
    actingBodyId: zod.coerce.number().optional(),
    actingBodyUnitIds: stringToListOfNumber.optional(),
    actingBodyCommandIds: stringToListOfNumber.optional(),
    overdueUnits: stringToBoolean.optional(),
    teamCategory: stringToListOfEnum(zod.enum(teamNameCategory)).optional(),
    isConnected: stringToBoolean.optional(),
    agentOnline: stringToBoolean.optional(),
    agentOffline: stringToBoolean.optional(),
});

export const UnitFormSchema = zod.object({
    id: zod.coerce.number().optional(),
}).optional();

export const PoliceStationFormSchema = zod.object({
    id: zod.coerce.number().optional(),
}).optional();

export const CityPropertyFormSchema = zod.object({
    id: zod.coerce.number().optional(),
}).optional();

export const TeamNameSchema = zod.object({
    id: zod.coerce.number().optional(),
    selectedTab: zod.enum(['details', 'live']).optional(),
}).optional();

export const DispatchOccurrenceDetailSchema = zod.object({
    occurrenceId: zod.coerce.number().optional(),
}).optional();
